"use client";

import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { GoogleTagManager } from "@next/third-parties/google";

/**
 * ConditionalGTMLoader Component
 *
 * BUSINESS LOGIC:
 * This component ensures GDPR compliance by only loading Google Tag Manager (GTM) and associated
 * tracking scripts (Google Analytics, Facebook Pixel, etc.) AFTER the user has explicitly
 * consented to analytics cookies. This prevents unauthorized data collection and transmission
 * to third parties, addressing violations of GDPR Article 9 and Dutch Telecommunications Act.
 *
 * LOGICAL FLOW:
 * 1. Component initializes with no consent (hasAnalyticsConsent = false)
 * 2. On mount and via polling, checks for "CookieConsent" cookie set by cookie banner
 * 3. Parses cookie value to extract analytics consent status
 * 4. Conditionally renders GTM only when analytics consent is granted
 * 5. GTM then loads Google Analytics, Facebook Pixel, and other configured tracking tags
 *
 * COOKIE FORMAT: {necessary:true,preferences:true,statistics:true,marketing:true}
 * - statistics:true indicates user consented to analytics cookies
 */
const ConditionalGTMLoader: React.FC = () => {
  // State tracks whether user has consented to analytics cookies
  const [hasAnalyticsConsent, setHasAnalyticsConsent] = useState<boolean>(false);

  useEffect(() => {
    /**
     * COOKIE CONSENT CHECKING LOGIC:
     *
     * Reads the "CookieConsent" cookie set by the cookie banner component.
     * Cookie format: {necessary:true,preferences:true,statistics:true,marketing:true}
     *
     * Business rule: Only load GTM if statistics consent is explicitly "true"
     */
    const checkConsent = () => {
      const consentCookie = Cookies.get("CookieConsent");

      if (consentCookie) {
        try {
          // Remove braces and parse key-value pairs
          const trimmed = consentCookie.trim();
          if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
            const content = trimmed.slice(1, -1); // Remove { }
            const pairs = content.split(","); // Split into ["necessary:true", "statistics:true", etc.]
            let statisticsConsent = false;

            // Parse each key-value pair to find analytics consent
            pairs.forEach((pair) => {
              const [key, value] = pair.split(":");
              // Check if user consented to statistics/analytics cookies
              if (key.trim() === "statistics" && value.trim() === "true") {
                statisticsConsent = true;
              }
            });

            // Update state based on parsed consent
            setHasAnalyticsConsent(statisticsConsent);
          }
        } catch (e) {
          console.error("Error parsing CookieConsent cookie:", e);
        }
      }
    };

    // Check consent immediately on component mount
    checkConsent();

    /**
     * POLLING MECHANISM:
     *
     * Since cookie consent can be given at any time via the cookie banner dialog,
     * we poll every second to detect changes. This ensures GTM loads immediately
     * when consent is granted, even if it happens after initial page load.
     */
    const interval = setInterval(checkConsent, 1000);

    // Cleanup polling on component unmount
    return () => clearInterval(interval);
  }, []);

  /**
   * CONDITIONAL RENDERING LOGIC:
   *
   * Only render the GoogleTagManager component if analytics consent has been granted.
   * This prevents any tracking scripts from loading before user consent.
   */
  if (!hasAnalyticsConsent) {
    return null;
  }

  /**
   * ENVIRONMENT VARIABLE VALIDATION:
   *
   * Ensure GTM ID is configured. Without it, tracking cannot function properly.
   */
  const gtmId = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;
  if (!gtmId) {
    console.warn("Google Tag Manager ID not configured");
    return null;
  }

  /**
   * GTM COMPONENT RENDERING:
   *
   * Once consent is confirmed and GTM ID is available, render the GoogleTagManager
   * component. This will load GTM scripts and any configured tags (GA4, Facebook Pixel, etc.)
   */
  return <GoogleTagManager gtmId={gtmId} />;
};

export default ConditionalGTMLoader;
